import { forwardRef, type HTMLAttributes } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const cardVariants = cva(
  // Base styles - clean, modern card design
  [
    'rounded-2xl bg-white dark:bg-slate-900',
    'transition-all duration-300 ease-out',
  ],
  {
    variants: {
      variant: {
        // Default elevated card
        elevated: [
          'shadow-soft',
          'border border-slate-200 dark:border-slate-700',
        ],
        // Flat card with border
        outlined: [
          'border-2 border-slate-200 dark:border-slate-700',
          'shadow-none',
        ],
        // Subtle background only
        filled: [
          'bg-slate-50 dark:bg-slate-800',
          'border border-slate-100 dark:border-slate-700',
        ],
        // Glass morphism effect
        glass: [
          'bg-white/70 dark:bg-slate-900/70 backdrop-blur-md',
          'border border-white/20 dark:border-slate-700/50',
          'shadow-soft',
        ],
        // Gradient accent border
        gradient: [
          'relative overflow-hidden',
          'border border-slate-200 dark:border-slate-700',
          'before:absolute before:inset-0 before:rounded-2xl',
          'before:bg-gradient-to-br before:from-accent-500/10 before:to-primary-500/10',
          'before:-z-10',
        ],
      },
      padding: {
        none: 'p-0',
        sm: 'p-4',
        md: 'p-6',
        lg: 'p-8',
      },
      interactive: {
        true: [
          'cursor-pointer',
          'hover:shadow-soft-md hover:-translate-y-0.5',
          'active:shadow-soft active:translate-y-0',
        ],
      },
    },
    defaultVariants: {
      variant: 'elevated',
      padding: 'none', // Changed to none since we control padding in content
    },
  }
)

export interface CardProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

// Card Header Component
interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  title?: string
  subtitle?: string
  action?: React.ReactNode
}

const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, title, subtitle, action, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('p-6 pb-0', className)}
        {...props}
      >
        <div className="flex items-start justify-between gap-4">
          {(title || subtitle) && (
            <div className="space-y-1">
              {title && (
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{title}</h3>
              )}
              {subtitle && (
                <p className="text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>
              )}
            </div>
          )}
          {children}
          {action && <div className="flex-shrink-0">{action}</div>}
        </div>
      </div>
    )
  }
)

CardHeader.displayName = 'CardHeader'

// Card Content Component
const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    return <div ref={ref} className={cn('p-6', className)} {...props} />
  }
)

CardContent.displayName = 'CardContent'

// Card Footer Component
const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('flex items-center gap-3 p-6 pt-0 border-t border-slate-200 dark:border-slate-700', className)}
        {...props}
      />
    )
  }
)

CardFooter.displayName = 'CardFooter'

// Main Card Component
const CardBase = forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, padding, interactive, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(cardVariants({ variant, padding, interactive, className }))}
        {...props}
      />
    )
  }
)

CardBase.displayName = 'Card'

// Create compound component
type CardComponent = typeof CardBase & {
  Header: typeof CardHeader
  Content: typeof CardContent
  Footer: typeof CardFooter
}

const Card = CardBase as CardComponent
Card.Header = CardHeader
Card.Content = CardContent
Card.Footer = CardFooter

export { Card, CardHeader, CardContent, CardFooter, cardVariants }
